import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { jwtDecode } from 'jwt-decode'

// User roles in the system
export type UserRole = 'admin' | 'partner' | 'user' | 'demo'

// Decoded JWT token payload
export interface TokenPayload {
  sub: string // user_id
  email: string
  role: UserRole
  customer_id?: string
  partner_id?: string
  exp: number
  iat: number
}

// Current user data
export interface User {
  id: string
  email: string
  firstName?: string
  lastName?: string
  fullName?: string
  role: UserRole
  customerId?: string
  partnerId?: string
  avatarUrl?: string
}

// Auth state interface
interface AuthState {
  // State
  accessToken: string | null
  refreshToken: string | null
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  
  // 2FA state
  requires2FA: boolean
  tempToken: string | null // Temporary token during 2FA flow
  
  // Actions
  setTokens: (accessToken: string, refreshToken?: string) => void
  setUser: (user: User) => void
  setLoading: (loading: boolean) => void
  set2FARequired: (required: boolean, tempToken?: string) => void
  logout: () => void
  checkAuth: () => boolean
  getDecodedToken: () => TokenPayload | null
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: true,
      requires2FA: false,
      tempToken: null,

      // Set tokens after successful login
      setTokens: (accessToken, refreshToken) => {
        // Also store in localStorage for API interceptor
        localStorage.setItem('access_token', accessToken)
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken)
        }
        
        // Decode token to get user info
        try {
          const decoded = jwtDecode<TokenPayload>(accessToken)
          const user: User = {
            id: decoded.sub,
            email: decoded.email,
            role: decoded.role,
            customerId: decoded.customer_id,
            partnerId: decoded.partner_id,
          }
          
          set({
            accessToken,
            refreshToken: refreshToken || get().refreshToken,
            user,
            isAuthenticated: true,
            isLoading: false,
            requires2FA: false,
            tempToken: null,
          })
        } catch (error) {
          console.error('Failed to decode token:', error)
          set({
            accessToken,
            refreshToken: refreshToken || get().refreshToken,
            isAuthenticated: true,
            isLoading: false,
          })
        }
      },

      // Set user data (from /me endpoint)
      setUser: (user) => {
        set({ user })
      },

      // Set loading state
      setLoading: (loading) => {
        set({ isLoading: loading })
      },

      // Set 2FA required state
      set2FARequired: (required, tempToken) => {
        set({
          requires2FA: required,
          tempToken: tempToken || null,
          isLoading: false,
        })
      },

      // Logout - clear all auth state
      logout: () => {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          requires2FA: false,
          tempToken: null,
        })
      },

      // Check if current auth is valid
      checkAuth: () => {
        const token = get().accessToken || localStorage.getItem('access_token')
        
        if (!token) {
          set({ isAuthenticated: false, isLoading: false })
          return false
        }

        try {
          const decoded = jwtDecode<TokenPayload>(token)
          const now = Date.now() / 1000
          
          // Check if token is expired
          if (decoded.exp < now) {
            get().logout()
            return false
          }
          
          // Token is valid, update state if needed
          if (!get().isAuthenticated) {
            get().setTokens(token)
          }
          
          set({ isLoading: false })
          return true
        } catch {
          get().logout()
          return false
        }
      },

      // Get decoded token payload
      getDecodedToken: () => {
        const token = get().accessToken
        if (!token) return null
        
        try {
          return jwtDecode<TokenPayload>(token)
        } catch {
          return null
        }
      },
    }),
    {
      name: 'bonnie-auth',
      // Only persist tokens, not loading states
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
)

export default useAuthStore
